import { useQueryClient } from '@tanstack/react-query';
import { useGetPreferenceOptions } from '@hungryroot/preference-options';
import {
  type LikeDislikeTagsRatings,
  type PatchedCustomerTagList,
  type PreferenceOption,
  type PreferenceOptionKey,
} from '@hungryroot/rest';
import { usePatchCustomerTags } from '@hungryroot/rest-hooks';
import { getCustomerTagsQueryKey, useCustomerTags } from '@hungryroot/tags';
import { TagPreference } from '../components/ControlTagSwitch/ControlTagSwitch';

type TagPreferenceTuple = [
  LikeDislikeTagsRatings | undefined,
  PreferenceOption,
];

interface FoodProfilePageParams {
  customerId: number;
  page: PreferenceOptionKey;
  tagExclusions?: number[];
}

/** Drives the content for a food profile section that uses the `preference_options` endpoint */
export function useFoodProfilePage({
  customerId,
  page,
  tagExclusions,
}: FoodProfilePageParams) {
  /** Gets the tags that we want to render for all pages */
  const pagesData = useGetPreferenceOptions();

  /** List of tags that should be displayed as a switch on the selected page */
  const tagsForPageDisplay = pagesData?.data?.pages
    .find((pageData) => pageData.key === page)
    ?.options?.filter((option) => !tagExclusions?.includes(option.tag_id));

  const tagsToRequest = tagsForPageDisplay?.map((option) => option.tag_id);

  /* Get the customers preference data for the tags that we want to render */
  const { data: customerTags } = useCustomerTags(tagsToRequest);

  const patchCustomerTagsMutation = usePatchCustomerTags();

  const preferencesAndTags: undefined | TagPreferenceTuple[] =
    tagsForPageDisplay?.map((displayTag) => [
      customerTags?.results.find((data) => data.tag === displayTag.tag_id)
        ?.preference,
      displayTag,
    ]);

  const queryClient = useQueryClient();

  const onSwitchSelect = (
    tag: Pick<PreferenceOption, 'tag_id'>,
    selected: boolean
  ) => {
    const payload: PatchedCustomerTagList = {
      customer_id: customerId,
      tag: tag.tag_id,
      preference: selected ? TagPreference.Selected : TagPreference.NotSelected,
    };

    return patchCustomerTagsMutation.mutate(
      { data: [payload] },
      {
        onSuccess: () => {
          const key = getCustomerTagsQueryKey(tagsToRequest);
          queryClient.invalidateQueries({
            queryKey: key,
          });
        },
      }
    );
  };

  const subtitle = customerTags?.results
    .filter((pref) => pref.preference === TagPreference.Selected)
    .map(
      (tag) =>
        tagsForPageDisplay?.find(
          (pageDisplayTag) => pageDisplayTag.tag_id === tag.tag
        )?.name
    )
    .filter(Boolean)
    .join(', ');

  return {
    subtitle,
    preferencesAndTags,
    onSwitchSelect,
  };
}
